import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { BasicPopover, BasicRadioGroup, BasicTable } from '../../assets/common';
import { filter_feedback } from '../../assets/img';
import styles from './ModalFeedbackSummary.module.scss';
import talentProfileApi from '../../services/talentProfiles';
import { Radio, Space } from 'antd';
import { MODULE_FILTER_TABLE, VIEW_VERBATIM_FEEDBACK } from '../../utils/constants';
import CustomFilterTable from '../CustomFilterTable';
import { mapEdgeName, paginationConfig, sortingEdgeBasedOnName } from '../../utils/helper';
import _ from 'lodash';

const PER_PAGE = 10;

const ModalFeedbackSummary = ({ show, setShow, roleUser, id, tokenTalentProfile }) => {
  const [sortNewest, setSortNewest] = useState('descend');
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFeedbackSummary, setLoadingFeedbackSummary] = useState(false);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [page, setPage] = useState(1);
  const [radioVerbatimFeedback, setRadioVerbatimFeedback] = useState(VIEW_VERBATIM_FEEDBACK.EDGE);
  const [listSelectTypeEdge, setListSelectTypeEdge] = useState([]);
  const [filtersOption, setFiltersOption] = useState([]);
  const [currentToggleItem, setCurrentToggleItem] = useState(null);
  const isInit = useRef(false);

  const handleCheckDropdownType = async (query) => {
    if (radioVerbatimFeedback === VIEW_VERBATIM_FEEDBACK.ATTRIBUTES) {
      return await talentProfileApi.getLeaderShipCompetenciesAttributeDropdownList(query);
    }

    return await talentProfileApi.getLeaderShipCompetenciesEdgeDropdownList(query);
  };

  const fetchDropdownOptions = async () => {
    try {
      const query = {
        userId: id,
        roleUser: roleUser,
        tokenTalentProfile: tokenTalentProfile,
      };

      const fetchedOptions = await handleCheckDropdownType(query);

      if (fetchedOptions.status === 200) {
        const data = fetchedOptions.data.result;
        const formattedOptions = data.map((option, index) => {
          let text = option.value;
          let order = {
            parentIndex: index,
            childIndex: index,
          };

          if (radioVerbatimFeedback === VIEW_VERBATIM_FEEDBACK.EDGE) {
            text = mapEdgeName(option.value);
            order = sortingEdgeBasedOnName(option.value);
          }

          return {
            value: option.value,
            text,
            isChecked: option.value,
            order,
          };
        });
        isInit.current = true;

        const sortedFilterOptions = _.orderBy(formattedOptions, ['order.parentIndex', 'order.childIndex'], ['asc', 'asc']);

        setListSelectTypeEdge(sortedFilterOptions.map((i) => i.value));
        setFiltersOption(sortedFilterOptions);
      }
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    }
  };

  useEffect(() => {
    if (show) {
      fetchDropdownOptions();
    }
  }, [show, radioVerbatimFeedback]);

  const handleClose = () => {
    setShow(false);
    setSortNewest('descend');
    setFeedbacks([]);
    setTotalFeedbacks(0);
    setPage(1);
    setRadioVerbatimFeedback(VIEW_VERBATIM_FEEDBACK.EDGE);
    setListSelectTypeEdge([]);
    setFiltersOption([]);
  };

  const handleCheckType = async (query) => {
    if (radioVerbatimFeedback === VIEW_VERBATIM_FEEDBACK.ATTRIBUTES) {
      return await talentProfileApi.getLeaderShipCompetenciesAttributeDisplay(query);
    }

    return await talentProfileApi.getLeaderShipCompetenciesEdgeDisplay(query);
  };

  const fetchFeedbacks = async () => {
    try {
      setLoadingFeedbackSummary(true);
      const query = {
        userId: id,
        roleUser: roleUser,
        tokenTalentProfile: tokenTalentProfile,
        take: PER_PAGE,
        page: page,
        isDESC: sortNewest === 'descend',
        filter: listSelectTypeEdge,
      };

      const fetchedFeedbacks = await handleCheckType(query);

      if (fetchedFeedbacks.status === 200) {
        const data = fetchedFeedbacks.data.result;

        setTotalFeedbacks(data.totalFeedbacks);
        setFeedbacks(data.feedbacks);
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setLoadingFeedbackSummary(false);
    }
  };

  useEffect(() => {
    if (isInit.current) {
      fetchFeedbacks();
    }
  }, [page, radioVerbatimFeedback, sortNewest, listSelectTypeEdge, isInit.current]);

  const handleSort = (isDesc) => {
    setSortNewest(isDesc);
    setPage(1);
  };

  const onApplyFilters = (selectedKeys) => {
    setListSelectTypeEdge(selectedKeys);
  };

  const onChangeRadioVerbatim = async ({ target: { value } }) => {
    setRadioVerbatimFeedback(value);
    setPage(1);
    setFeedbacks([]);
    setSortNewest('descend');
    setListSelectTypeEdge([]);
    setFiltersOption([]);
  };

  const columns = [
    {
      title: radioVerbatimFeedback === VIEW_VERBATIM_FEEDBACK.EDGE ? VIEW_VERBATIM_FEEDBACK.EDGE : 'Attributes',
      dataIndex: 'label',
      align: 'left',
      width: '20%',
      render: (_, record) => {
        if (radioVerbatimFeedback === VIEW_VERBATIM_FEEDBACK.EDGE) {
          return <span>{mapEdgeName(record?.label)}</span>;
        }

        return <span>{record?.label || ''}</span>;
      },
      filterIcon: <img src={filter_feedback} alt="" />,
      filterDropdown: ({ _clearFilters, close }) => {
        return (
          <CustomFilterTable
            setSelectedKeys={setListSelectTypeEdge}
            selectedKeys={listSelectTypeEdge}
            onApplyFilters={onApplyFilters}
            filters={filtersOption}
            close={close}
            filtername={radioVerbatimFeedback.toLowerCase()}
            moduleName={MODULE_FILTER_TABLE.VERBATIM_FEEDBACK}
            customStyle={{ width: 320 }}
          />
        );
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      align: 'left',
      width: '8%',
      render: (_, record) => {
        return <span>{record?.source || ''}</span>;
      },
    },
    {
      title: 'Year',
      dataIndex: 'year',
      align: 'left',
      width: '8%',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (_, record) => {
        return <span>{record.year || ''}</span>;
      },
    },
    {
      title: 'Feedback Dimension',
      dataIndex: 'feedback_dimension',
      align: 'left',
      width: '20%',
      render: (_, record) => {
        return <div style={{ minWidth: '200px' }}>{record?.feedback_dimension || ''}</div>;
      },
    },
    {
      title: 'Score',
      dataIndex: 'score',
      align: 'left',
      width: '8%',
      render: (_, record) => {
        return <span>{!isNaN(record?.score) ? record?.score : ''}</span>;
      },
    },
    {
      title: 'Excerpt Sentence',
      dataIndex: 'excerpt_sentence',
      align: 'left',
      width: '420',
      render: (_, record) => {
        return (
          <div
            style={{
              width: '400px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <BasicPopover
              content={<div>{record?.feedback_extracted}</div>}
              trigger="click"
              placement="bottom"
              getPopupContainer={(trigger) => trigger}
              open={currentToggleItem === record?.id}
              onOpenChange={(open) => {
                if (!open) {
                  setCurrentToggleItem(null);
                }
              }}
              styles={{
                minWidth: '420',
                fontSize: '12px',
                lineHeight: '18px',
                fontWeight: 600,
                fontFamily: `'Inter', sans-serif`,
                padding: '12px',
                borderRadius: '4px',
                bg: '#000',
                color: '#fff',
                hideArrow: false,
              }}
              overlayInnerStyle={{
                maxWidth: '450px', // Stops it from expanding endlessly
                maxHeight: '300px', // Adds scrollbar if text is huge
                overflowY: 'auto',
              }}
            >
              <div
                onClick={() => {
                  setCurrentToggleItem((prev) => {
                    return prev !== record?.id ? record?.id : null;
                  });
                }}
              >
                {record?.excerpt_sentence || ''}
              </div>
            </BasicPopover>
          </div>
        );
      },
    },
  ];

  const pagination = useMemo(() => {
    const onChange = (pageNum) => {
      setPage(pageNum);
    };
    return paginationConfig(totalFeedbacks || 0, page, onChange, PER_PAGE);
  }, [totalFeedbacks, page, PER_PAGE]);

  return (
    <div>
      <Modal show={show} onHide={handleClose} animation={false} dialogClassName={styles.containerModal} data-testid="edit-tagging">
        <Modal.Header closeButton className={styles.editTagging__header}>
          <Modal.Title className={styles.editTagging__title}>View Verbatim Feedback</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0 30px 20px' }}>
          <div className={styles.verbatimFeedback}>
            <BasicRadioGroup onChange={onChangeRadioVerbatim} value={radioVerbatimFeedback}>
              <Space direction="horizontal" className={styles.radioTitle}>
                {Object.values(VIEW_VERBATIM_FEEDBACK).map((item) => (
                  <Radio key={item} value={item}>
                    {item}
                  </Radio>
                ))}
              </Space>
            </BasicRadioGroup>
            <BasicTable
              key={radioVerbatimFeedback}
              className={styles.verbatimFeedback}
              rowKey={'id'}
              dataSource={feedbacks?.map((i, index) => {
                return {
                  ...i,
                  index,
                };
              })}
              columns={columns}
              pagination={pagination}
              loading={loadingFeedbackSummary}
              styles={{ headerColor: '#8896A7', borderRadius: 6 }}
              getPopupContainer={(trigger) => trigger.parentElement}
              onChange={(pagination, filters, sorter) => {
                if (sorter.order) {
                  handleSort(sorter.order);
                }

                if (pagination.current && sorter.order === sortNewest) {
                  setPage(pagination.current);
                }
              }}
            />
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};
export default ModalFeedbackSummary;
